import { BarDatum } from '@nivo/bar';
import { BarItem } from '@nivo/bar';
import React, { useCallback, useEffect, useMemo } from 'react';
import { BarItemProps } from '@nivo/bar/dist/types/types';
import { NoopTooltip } from 'apps/sigma2-ui/src/components/ui/Tooltip';
import { animated } from '@react-spring/web';
import { useSpringValue } from '@react-spring/web';
import { v4 as uuidv4 } from 'uuid';

export const KeyFactorsChartBar = ({
  barProps,
  onMouseEnter,
  onMouseLeave,
  onClick,
}: {
  barProps: BarItemProps<BarDatum>;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  tooltip?: React.ReactNode;
  shouldDisplayVertically: boolean;
  hoverEffect: boolean;
  onClick?: (barProps: BarItemProps<BarDatum>) => void;
}) => {
  const { value } = barProps.bar.data;

  // Place the bottom of the tooltip 30px bellow the bar height
  const barHeight = barProps.bar.height;
  const tooltipYPosition = barHeight - 30;

  const minBarHeight = barHeight < 2 ? 2 : barHeight;

  const heightSpring = useSpringValue(minBarHeight);
  const transformSpring = useSpringValue('');

  useEffect(() => {
    heightSpring.set(minBarHeight);
  }, [heightSpring, minBarHeight]);

  const newBarProps = useMemo(
    () => ({
      ...barProps,
      bar: {
        ...barProps.bar,
        y: barHeight === 0 ? -100 : barProps.bar.y,
      },
    }),
    [barHeight, barProps],
  );

  const handleOnHover = useCallback(() => {
    onMouseEnter();
  }, [onMouseEnter]);

  const handleOnHoverLeave = useCallback(() => {
    onMouseLeave();
  }, [onMouseLeave]);

  const handleOnClick = useCallback(() => {
    onClick?.(barProps);
  }, [onClick, barProps]);

  const barItemStyle = useMemo(
    () => ({
      ...barProps.style,
      transform: transformSpring,
      height: heightSpring,
    }),
    [barProps.style, heightSpring, transformSpring],
  );

  const popperProps = useMemo(
    () => ({
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [-tooltipYPosition, -10],
          },
        },
      ],
    }),
    [tooltipYPosition],
  );

  const uuid = uuidv4();

  const transformTransparentX = barProps.bar.x;
  const transformTransparentY =
    barHeight < 2 ? barProps.bar.y - 50 : barProps.bar.y;
  const transparentBarHeight = barHeight < 2 ? 50 : barProps.bar.height;

  // extra Y axis translate so small bars (bellow 2px height) are visible on
  // top of the 0% line
  const extraYTranslate = value >= 0 ? 12 : -12;
  const transform = `translate(${barProps.bar.x}, ${barProps.bar.y - extraYTranslate})`;

  const bar = useMemo(() => {
    return (
      <g
        data-testid={
          barProps.ariaLabel && barProps.ariaLabel(barProps.bar.data)
        }
      >
        <g
          transform={`translate(${transformTransparentX}, ${transformTransparentY})`}
        >
          <rect
            width={barProps.style.width.get()}
            height={`${transparentBarHeight}px`}
            fill="transparent"
          />
        </g>

        <animated.g transform={transform}>
          <animated.g clipPath={`url(#${uuid})`}>
            <BarItem {...newBarProps} style={barItemStyle} borderRadius={4} />
          </animated.g>
        </animated.g>
      </g>
    );
  }, [
    barItemStyle,
    barProps,
    newBarProps,
    transform,
    transformTransparentX,
    transformTransparentY,
    transparentBarHeight,
    uuid,
  ]);

  return (
    <NoopTooltip
      title={<></>}
      placement="right-end"
      onOpen={handleOnHover}
      onClose={handleOnHoverLeave}
      onClick={handleOnClick}
      PopperProps={popperProps}
    >
      {bar}
    </NoopTooltip>
  );
};
